import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, BookOpen, Repeat, Clock, CheckCircle } from 'lucide-react';

// Real prayer position images - User provided illustrations
const positionImages = {
  tekbir: "https://customer-assets.emergentagent.com/job_mosque-portal-8/artifacts/4jyj62fc_IMG_0354.jpeg",
  qijam: "https://customer-assets.emergentagent.com/job_mosque-portal-8/artifacts/79dini0i_IMG_0355.jpeg",
  ruku: "https://customer-assets.emergentagent.com/job_mosque-portal-8/artifacts/wopx3m8g_IMG_0356.jpeg",
  qijamPasRuku: "https://customer-assets.emergentagent.com/job_mosque-portal-8/artifacts/szetp5db_IMG_0357.jpeg",
  sexhde: "https://customer-assets.emergentagent.com/job_mosque-portal-8/artifacts/wsei7iwx_IMG_0358.jpeg",
  ulje: "https://customer-assets.emergentagent.com/job_mosque-portal-8/artifacts/1ljomdo9_IMG_0359.jpeg",
  sexhde2: "https://customer-assets.emergentagent.com/job_mosque-portal-8/artifacts/8y3cvk52_IMG_0360.jpeg",
  teshehud: "https://customer-assets.emergentagent.com/job_mosque-portal-8/artifacts/rebvc03v_IMG_0361.jpeg",
  selam: "https://customer-assets.emergentagent.com/job_mosque-portal-8/artifacts/cbu9bzym_IMG_0365.jpeg",
};

// Prayer position guide with illustrations
const positionGuide = {
  tekbir: {
    name: "Tekbiri Fillestar",
    description: "Ngrij duart tek veshët dhe thuaj 'Allahu Ekber'",
    image: positionImages.tekbir
  },
  qijam: {
    name: "Kijami (Qëndrimi)",
    description: "Qëndro drejt, dora e djathtë mbi të majtën në gjoks",
    image: positionImages.qijam
  },
  ruku: {
    name: "Rukuja (Përkulja)",
    description: "Përkulju me duart mbi gjunjë, shpina e drejtë",
    image: positionImages.ruku
  },
  qijamPasRuku: {
    name: "Ngritja pas Rukusë",
    description: "Ngrihu drejt dhe thuaj 'SemiAllahu limen hamideh'",
    image: positionImages.qijamPasRuku
  },
  sexhde: {
    name: "Sexhdja",
    description: "7 gjymtyrë në tokë: balli, hundët, duart, gjunjët, gishtërinjtë",
    image: positionImages.sexhde
  },
  ulje: {
    name: "Xhelsa (Ulja)",
    description: "Ulu mes dy sexhdeve, duart mbi gjunjë",
    image: positionImages.ulje
  },
  sexhde2: {
    name: "Sexhdja e Dytë",
    description: "Përsërit sexhden, thuaj 'Subhane Rabbijel A'la' 3x",
    image: positionImages.sexhde2
  },
  teshehud: {
    name: "Teshehudi",
    description: "Ulu për teshehudin, lexo Ettehijatin",
    image: positionImages.teshehud
  },
  selam: {
    name: "Selami",
    description: "Kthe kokën djathtas dhe majtas: 'Es-selamu alejkum'",
    image: positionImages.selam
  }
};

// Prayer guides with photos
const prayerGuides = {
  imsaku: {
    name: "Imsaku",
    arabicName: "الإمساك",
    description: "Imsaku është koha kur ndalon ngrënia dhe pirja për agjërimin. Nuk ka namaz specifik - është koha e fundit për syfyr.",
    isNotPrayer: true,
    color: "from-slate-600 to-slate-800",
    image: "https://images.unsplash.com/photo-1564769625905-50e93615e769?w=600&h=300&fit=crop",
    info: [
      "Imsaku fillon rreth 10-15 minuta para Sabahut",
      "Është koha e fundit për të ngrënë dhe pirë para agjërimit",
      "Rekomandohet të hani syfyr dhe të bëni dua",
      "Pas Imsakut fillon koha e Sabahut"
    ]
  },
  sabahu: {
    name: "Namazi i Sabahut (Fajr)",
    arabicName: "صلاة الفجر",
    description: "Namazi i Sabahut ka 2 rekate farz. Para tij falen 2 rekate sunet të rëndësishme.",
    totalRakats: 4,
    color: "from-orange-500 to-amber-600",
    image: positionImages.tekbir,
    structure: [
      { type: "Sunet", rakats: 2, description: "Shumë të rëndësishme" },
      { type: "Farz", rakats: 2, description: "Obligative" }
    ],
    steps: [
      { title: "Nijeti", description: "Bëj nijet për 2 rekate farz të Sabahut, drejtohu nga Kibla.", position: "qijam" },
      { title: "Tekbiri", description: "Ngrij duart tek veshët, thuaj 'Allahu Ekber', vendos duart në gjoks.", position: "tekbir" },
      { title: "Kijami", description: "Lexo Fatihanë dhe një sure tjetër (Ihlas, Felek, ose Nas).", position: "qijam" },
      { title: "Rukuja", description: "Thuaj 'Allahu Ekber', përkulju, thuaj 'Subhane Rabbijel Adhim' 3x.", position: "ruku" },
      { title: "Ngritja", description: "Ngrihu dhe thuaj 'SemiAllahu limen hamideh, Rabbena lekel hamd'.", position: "qijamPasRuku" },
      { title: "Sexhdja 1", description: "Thuaj 'Allahu Ekber', bie në sexhde, thuaj 'Subhane Rabbijel A'la' 3x.", position: "sexhde" },
      { title: "Ulja", description: "Ngrihu në ulje, thuaj 'Rabbigfirli' 2x.", position: "ulje" },
      { title: "Sexhdja 2", description: "Bie përsëri në sexhde, thuaj 'Subhane Rabbijel A'la' 3x.", position: "sexhde2" },
      { title: "Rekati 2", description: "Ngrihu, përsërit: Fatiha, sure, ruku, 2 sexhde.", position: "qijam" },
      { title: "Teshehudi", description: "Ulu, lexo Ettehijatin dhe salavatet.", position: "ulje" },
      { title: "Selami", description: "Kthe kokën djathtas dhe majtas: 'Es-selamu alejkum'.", position: "selam" }
    ],
    timing: "Nga agimi deri në lindjen e diellit",
    importance: "Suneti i Sabahut është më i mirë se dynjaja!"
  },
  dreka: {
    name: "Namazi i Drekës (Dhuhr)",
    arabicName: "صلاة الظهر",
    description: "Namazi i Drekës ka 4 rekate farz, 4 sunet para dhe 2 pas.",
    totalRakats: 10,
    color: "from-yellow-500 to-orange-500",
    image: positionImages.qijam,
    structure: [
      { type: "Sunet", rakats: 4, description: "Para farzit" },
      { type: "Farz", rakats: 4, description: "Obligative" },
      { type: "Sunet", rakats: 2, description: "Pas farzit" }
    ],
    steps: [
      { title: "Nijeti", description: "Bëj nijet për 4 rekate farz të Drekës.", position: "qijam" },
      { title: "Tekbiri", description: "Ngrij duart tek veshët dhe thuaj 'Allahu Ekber'.", position: "tekbir" },
      { title: "Rekati 1", description: "Lexo Fatiha + sure, ruku, 2 sexhde.", position: "ruku" },
      { title: "Rekati 2", description: "Fatiha + sure, ruku, 2 sexhde. Pastaj ulu.", position: "sexhde" },
      { title: "Teshehudi 1", description: "Lexo vetëm Ettehijatin, ngrihu për rekatin 3.", position: "ulje" },
      { title: "Rekatet 3-4", description: "Lexo VETËM Fatihanë (pa sure). Ruku, sexhde.", position: "qijam" },
      { title: "Teshehudi 2", description: "Lexo Ettehijatin, salavatet, dua.", position: "ulje" },
      { title: "Selami", description: "Jep selam djathtas dhe majtas.", position: "selam" }
    ],
    timing: "Nga zeniti deri kur hija dyfishohet",
    importance: "Namazi i mesditës për punëtorët"
  },
  ikindia: {
    name: "Namazi i Ikindisë (Asr)",
    arabicName: "صلاة العصر",
    description: "Namazi i Ikindisë ka 4 rekate farz.",
    totalRakats: 4,
    color: "from-amber-600 to-orange-700",
    image: positionImages.ruku,
    structure: [
      { type: "Farz", rakats: 4, description: "Obligative" }
    ],
    steps: [
      { title: "Nijeti", description: "Bëj nijet për 4 rekate farz të Ikindisë.", position: "qijam" },
      { title: "Tekbiri", description: "Ngrij duart tek veshët dhe thuaj 'Allahu Ekber'.", position: "tekbir" },
      { title: "Rekatet 1-2", description: "Fatiha + sure, ruku, sexhde. Tesheud i parë.", position: "ruku" },
      { title: "Rekatet 3-4", description: "Vetëm Fatiha. Ruku, sexhde.", position: "sexhde" },
      { title: "Teshehudi", description: "Tesheud i fundit me salavate.", position: "ulje" },
      { title: "Selami", description: "Selam djathtas dhe majtas.", position: "selam" }
    ],
    timing: "Deri në perëndim të diellit",
    importance: "Kush e lë, veprat shkojnë kot!"
  },
  akshami: {
    name: "Namazi i Akshamit (Maghrib)",
    arabicName: "صلاة المغرب",
    description: "Namazi i Akshamit ka 3 rekate farz dhe 2 sunet pas.",
    totalRakats: 5,
    color: "from-orange-600 to-red-600",
    image: positionImages.sexhde,
    structure: [
      { type: "Farz", rakats: 3, description: "Obligative" },
      { type: "Sunet", rakats: 2, description: "Pas farzit" }
    ],
    steps: [
      { title: "Nijeti", description: "Bëj nijet për 3 rekate farz të Akshamit.", position: "qijam" },
      { title: "Tekbiri", description: "Ngrij duart tek veshët dhe thuaj 'Allahu Ekber'.", position: "tekbir" },
      { title: "Rekatet 1-2", description: "Fatiha + sure, ruku, sexhde. Tesheud i parë.", position: "ruku" },
      { title: "Rekati 3", description: "Vetëm Fatiha. Ruku, sexhde.", position: "sexhde" },
      { title: "Teshehudi", description: "Tesheud i fundit.", position: "ulje" },
      { title: "Selami", description: "Selam djathtas dhe majtas.", position: "selam" }
    ],
    timing: "Menjëherë pas perëndimit - koha e shkurtër!",
    importance: "Fale shpejt pas perëndimit"
  },
  jacia: {
    name: "Namazi i Jacisë (Isha)",
    arabicName: "صلاة العشاء",
    description: "Namazi i Jacisë ka 4 farz, 4 sunet para, 2 pas + 3 vitër.",
    totalRakats: 13,
    color: "from-indigo-600 to-purple-700",
    image: positionImages.selam,
    structure: [
      { type: "Sunet", rakats: 4, description: "Para farzit" },
      { type: "Farz", rakats: 4, description: "Obligative" },
      { type: "Sunet", rakats: 2, description: "Pas farzit" },
      { type: "Vitër", rakats: 3, description: "Vaxhib" }
    ],
    steps: [
      { title: "Nijeti", description: "Bëj nijet për 4 rekate farz të Jacisë.", position: "qijam" },
      { title: "Tekbiri", description: "Ngrij duart tek veshët dhe thuaj 'Allahu Ekber'.", position: "tekbir" },
      { title: "Rekatet 1-2", description: "Fatiha + sure, ruku, sexhde. Tesheud i parë.", position: "ruku" },
      { title: "Rekatet 3-4", description: "Vetëm Fatiha. Ruku, sexhde.", position: "sexhde" },
      { title: "Teshehudi", description: "Tesheud i fundit.", position: "ulje" },
      { title: "Selami", description: "Selam. Pastaj fal sunetet dhe vitrin.", position: "selam" }
    ],
    timing: "Nga errësimi deri në agim",
    importance: "Vitri është vaxhib (Hanefi)"
  }
};

const PrayerGuideModal = ({ isOpen, onClose, prayerKey }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showPositions, setShowPositions] = useState(false);
  
  if (!isOpen || !prayerKey) return null;
  
  const guide = prayerGuides[prayerKey];
  if (!guide) return null;

  const handleClose = () => {
    setCurrentStep(0);
    setShowPositions(false);
    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-end sm:items-center justify-center"
        onClick={handleClose}
        data-testid="prayer-guide-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="bg-white w-full sm:max-w-2xl sm:mx-4 max-h-[95vh] sm:max-h-[90vh] rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="prayer-guide-modal"
        >
          {/* Header with Image */}
          <div className="relative h-32 sm:h-40">
            <img 
              src={guide.image} 
              alt={guide.name}
              className="w-full h-full object-cover"
            />
            <div className={`absolute inset-0 bg-gradient-to-t ${guide.color} opacity-80`} />
            <div className="absolute inset-0 p-4 flex flex-col justify-end text-white">
              <p className="text-xs sm:text-sm opacity-80">{guide.arabicName}</p>
              <h2 className="text-xl sm:text-2xl font-heading font-bold">{guide.name}</h2>
              {guide.totalRakats && (
                <p className="text-sm opacity-90">{guide.totalRakats} rekate gjithsej</p>
              )}
            </div>
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 w-8 h-8 sm:w-10 sm:h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white"
              data-testid="prayer-guide-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[calc(95vh-200px)] sm:max-h-[calc(90vh-220px)]">
            <p className="text-gray-600 text-sm sm:text-base mb-4">{guide.description}</p>

            {guide.isNotPrayer ? (
              <div className="space-y-2">
                {guide.info.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                    <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-gray-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            ) : (
              <>
                {/* Rakat Structure */}
                <div className="mb-4">
                  <h3 className="text-sm font-bold text-gray-500 uppercase mb-2 flex items-center gap-2">
                    <Repeat className="w-4 h-4" />
                    Struktura
                  </h3>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {guide.structure.map((item, index) => (
                      <div
                        key={index}
                        className={`flex-shrink-0 px-4 py-3 rounded-xl text-center min-w-[80px] ${
                          item.type === 'Farz'
                            ? 'bg-primary/10 border-2 border-primary'
                            : item.type === 'Vitër'
                            ? 'bg-amber-100 border-2 border-amber-400'
                            : 'bg-gray-100 border-2 border-gray-200'
                        }`}
                      >
                        <span className="text-2xl font-bold text-gray-800">{item.rakats}</span>
                        <p className={`text-xs font-semibold ${
                          item.type === 'Farz' ? 'text-primary' : 
                          item.type === 'Vitër' ? 'text-amber-600' : 'text-gray-500'
                        }`}>{item.type}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Prayer Positions - Expandable */}
                <button
                  onClick={() => setShowPositions(!showPositions)}
                  className="w-full mb-4 p-3 bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl flex items-center justify-between"
                >
                  <span className="font-semibold text-gray-700 text-sm">📸 Shiko Pozicionet e Namazit</span>
                  <ChevronRight className={`w-5 h-5 text-gray-500 transition-transform ${showPositions ? 'rotate-90' : ''}`} />
                </button>

                <AnimatePresence>
                  {showPositions && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mb-4 overflow-hidden"
                    >
                      <div className="grid grid-cols-4 gap-2">
                        {Object.entries(positionGuide).map(([key, pos]) => (
                          <div key={key} className="rounded-xl overflow-hidden shadow-md">
                            <img src={pos.image} alt={pos.name} className="w-full h-20 sm:h-28 object-cover" />
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Steps */}
                <div>
                  <h3 className="text-sm font-bold text-gray-500 uppercase mb-3 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Hapat ({guide.steps.length})
                  </h3>
                  
                  {/* Step Pills */}
                  <div className="flex gap-1.5 mb-3 overflow-x-auto pb-2 scrollbar-hide">
                    {guide.steps.map((step, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentStep(index)}
                        className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                          index === currentStep
                            ? `bg-gradient-to-r ${guide.color} text-white`
                            : index < currentStep
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {index < currentStep ? <CheckCircle className="w-3 h-3 inline mr-1" /> : null}
                        {index + 1}
                      </button>
                    ))}
                  </div>

                  {/* Current Step */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentStep}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="bg-gray-50 rounded-xl overflow-hidden"
                    >
                      <div className="flex">
                        {/* Position Image */}
                        <div className="w-24 sm:w-32 flex-shrink-0">
                          <img 
                            src={positionGuide[guide.steps[currentStep].position]?.image}
                            alt={guide.steps[currentStep].position}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {/* Step Info */}
                        <div className="flex-1 p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`w-5 h-5 rounded-full bg-gradient-to-r ${guide.color} text-white text-xs flex items-center justify-center font-bold`}>
                              {currentStep + 1}
                            </span>
                            <h4 className="font-bold text-gray-800 text-sm">{guide.steps[currentStep].title}</h4>
                          </div>
                          <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                            {guide.steps[currentStep].description}
                          </p>
                          <p className="text-xs text-primary mt-1 font-medium">
                            {positionGuide[guide.steps[currentStep].position]?.name}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>

                  {/* Step Navigation */}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                      disabled={currentStep === 0}
                      className={`flex-1 py-2 rounded-xl text-sm font-semibold flex items-center justify-center gap-1 ${
                        currentStep === 0 ? 'bg-gray-100 text-gray-400' : 'bg-gray-200 text-gray-700 active:scale-95'
                      }`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Mbrapa
                    </button>
                    <button
                      onClick={() => setCurrentStep(Math.min(guide.steps.length - 1, currentStep + 1))}
                      disabled={currentStep === guide.steps.length - 1}
                      className={`flex-1 py-2 rounded-xl text-sm font-semibold flex items-center justify-center gap-1 ${
                        currentStep === guide.steps.length - 1 
                          ? 'bg-green-500 text-white' 
                          : `bg-gradient-to-r ${guide.color} text-white active:scale-95`
                      }`}
                    >
                      {currentStep === guide.steps.length - 1 ? 'Fund' : 'Vazhdo'}
                      {currentStep < guide.steps.length - 1 && <ChevronRight className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Timing & Importance */}
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <div className="bg-blue-50 rounded-xl p-3">
                    <p className="text-xs font-bold text-blue-700 flex items-center gap-1 mb-1">
                      <Clock className="w-3 h-3" /> Koha
                    </p>
                    <p className="text-xs text-blue-600">{guide.timing}</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-3">
                    <p className="text-xs font-bold text-amber-700 mb-1">⭐ Rëndësia</p>
                    <p className="text-xs text-amber-600">{guide.importance}</p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Bottom Safe Area for Mobile */}
          <div className="p-3 bg-gray-50 border-t sm:hidden">
            <button
              onClick={handleClose}
              className={`w-full py-3 bg-gradient-to-r ${guide.color} text-white rounded-xl font-semibold`}
            >
              Mbyll
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PrayerGuideModal;
